import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription, timer } from 'rxjs';
import { debounceTime, groupBy, mergeMap } from 'rxjs/operators';

import * as PusherTypes from 'pusher-js';

import { WebsocketService } from '@core/services/websocket/websocket.service';

import { DashboardService } from './dashboard.service';
import { LibraryService } from './library.service';
import { ApprovalsService } from '@app/services/app/approvals.service';
import { ProjectsService } from './projects.service';
import { ProjectsFormsService } from './projects-forms.service';
import { VersionsViewService } from './versions.service';
import { AppService } from './app.service';
import { Session } from 'app/store/models/session.model';
import { UsersOnlineService } from './users-online.service';
import { AppCurrentSessionService } from './app-current-session.service';

import { environment } from '@environment/environment';

// class SessionPackage {
//     constructor(private session: Session){
//         console.log("session ---->", session);
//     }

//     empty = () : boolean => this.session == null;

//     organization = () : Objects.Organization => this.session.current.organization;

//     user = () : Objects.User => this.session.current.user;

//     options = () : any => this.session.options;

//     configuration = (): any => this.session.configuration; 
// }

@Injectable({
    providedIn: 'root'
})
export class AppBehaviorService {
    private readonly TIMER_UPDATE_FEEDBACKS = 500;
    private readonly TIMER_UPDATE_RELATED_VERSIONS = 5000; // dec-2025 set to 5s to avoid related reloads before version is ready
    private usrChannel: PusherTypes.Channel;
    private orgChannel: PusherTypes.Channel;
    // private onlineChannel: PusherTypes.PresenceChannel;

    private sessionSubscriptor: Subscription;
    private organizationSubscriptor: Subscription;
    private versionFeedbackEventsSubscriptor: Subscription;

    // private session: SessionPackage = null;
    private session: Session = null;
    private organization: number = null;

    constructor(
        private appSrv: AppService,
        private dashSrv: DashboardService,
        private libSrv: LibraryService,
        private approvalsSrv: ApprovalsService,
        private prjSrv: ProjectsService,
        private projectFormsSrv: ProjectsFormsService,
        private verSrv: VersionsViewService,
        private websocket: WebsocketService,
        private usrOnline: UsersOnlineService,
        private currentSessionSrv: AppCurrentSessionService
    ) {}
    
    start() {
        // this.appSubscriptor = this.appSrv.selectApp().subscribe((app: App) => this.init(app));
        this.sessionSubscriptor = this.appSrv.getSession().subscribe((session: Session) => this.init(session));
        
        this.appSrv.init();
    }

    end() {
        // unbind channels
        this.websocket.unbind(this.usrChannel);
        this.websocket.unbind(this.orgChannel);
        // this.websocket.unbind(this.onlineChannel);

        this.usrOnline.end();
        this.currentSessionSrv.unset();
        
        if(this.sessionSubscriptor != null)
            this.sessionSubscriptor.unsubscribe();

        if(this.organizationSubscriptor != null)
            this.organizationSubscriptor.unsubscribe();

        this.versionFeedbackEventsSubscriptor?.unsubscribe();
        
        this.session = null;
    }

    private init(session: Session) {

        // first time
        if(session == null)
            return;
        
        // check if change org
        if(this.session !== null && this.session.current.organization === session.current.organization)
            return;
        
        this.session = session;

        // fix 2024/03/12 <rafael@twonas.com>
        // put pusher config in environment
        // let config = this.session.configuration;
        // this.websocket.connect(config.websocket.pusher.key, config.websocket.pusher.cluster);
        this.websocket.connect();

        this.websocket.unbind(this.usrChannel);
        this.usrChannel = this.websocket.addChannel('private-usr_'+this.session.current.user.id);

        this.websocket.unbind(this.orgChannel);
        this.orgChannel = this.websocket.addChannel('private-org_'+this.session.current.organization.id);

        // Log all events on both channels for debugging purposes (uncomment if needed)
        // this.usrChannel.bind_global((event, data) => {
        //     console.log('[usrChannel]', event, data);
        // });
        // this.orgChannel.bind_global((event, data) => {
        //     console.log('[orgChannel]', event, data);
        // });

        this.usrOnline.start(session);
        this.currentSessionSrv.set(session);

        this.general();
        this.dashboard();
        this.library();

        // launch Request and Version events listeners
        this.bindOrganizationEvents();

        console.log("App behavior initialized");

        // Websocket disconnect
        this.websocket.getState$().subscribe(state => {
            if(state.current == 'unavailable'){
                this.appSrv.disconnect();
            }
        });


        // online presence channel listeners


    }

    // private initOrganization(org: number) { 

    // }

    private general() {
        this.usrChannel.bind('pusher:subscription_error', status => this.appSrv.setHttpError(status));
        this.usrChannel.bind('session.created', data => {
            if (!data || !data.sessionUuid) return;

            else {
                console.debug("New session detected!");
                // reload the app base url
                window.location.href = environment.appBasePath;
            }
        });
    }

    /**
     * oct-2026: one binding per organization event. Each section used to bind the events it needed on its own, so
     * an event bound in several sections ran several times (e.g. `version.updated` was bound in projects, library
     * and approvals, and two of them reloaded the open artwork: every event loaded the version, with all its
     * assets, comments and shares, twice). Add a new section's handler to the event's single binding here.
     */
    private bindOrganizationEvents() {
        this.orgChannel.bind('request.updated', data => {
            this.dashSrv.updateProject(+data.request, data.source_event);
            this.projectFormsSrv.updateProjectForms(+data.request, data.source_event);
            this.prjSrv.update(+data.request, data.source_event);
        });

        this.orgChannel.bind('request.messages.updated', data => {
            this.prjSrv.updateMessages(+data.request);
        });

        this.orgChannel.bind('request.version.uploaded', data => {
            // reload related versions with custom delay
            timer(this.TIMER_UPDATE_RELATED_VERSIONS).subscribe(() => {
                this.prjSrv.loadNewRelatedVersions(+data.request, +data.version);
            });
        });

        // source_event is not coming on new events
        this.orgChannel.bind('version.updated', data => {
            this.prjSrv.updateRelatedVersion(+data.version);
            this.libSrv.updateVersion(+data.version);
            this.approvalsSrv.updateVersion(+data.version, data.source_event);
            this.verSrv.update(+data.version);
        });

        this.orgChannel.bind('version.messages.updated', data => {
            this.verSrv.updateMessages(+data.version);
        });

        // version.feedbacks.updated and version.upload-version.updated
        this.versionFeedbackEvents();
    }

    /**
     * oct-2026: version.feedbacks.updated and version.upload-version.updated were bound in four places, each with its
     * own timer, so one action of another user reloaded the same data several times. The backend sends both events
     * for one Feedback action: every burst of events of a version waits TIMER_UPDATE_FEEDBACKS after the last one and
     * reloads each section once. An upload sends one upload-version event for the version the request was on and one for
     * the new version, so each of them gets its own burst. Both types refresh every section: the list bubbles show only
     * Approval/Comments requests today, but the artwork details page shows every type.
     */
    private versionFeedbackEvents() {
        this.versionFeedbackEventsSubscriptor?.unsubscribe();
        const events$ = new Subject<number>();

        this.orgChannel.bind('version.feedbacks.updated', data => events$.next(+data.version));
        this.orgChannel.bind('version.upload-version.updated', data => events$.next(+data.version));

        this.versionFeedbackEventsSubscriptor = events$.pipe(
            groupBy((versionId: number) => versionId),
            mergeMap((versionEvents$) => versionEvents$.pipe(debounceTime(this.TIMER_UPDATE_FEEDBACKS)))
        ).subscribe((versionId: number) => {
            // the artwork details page needs the user to update the share permissions
            this.verSrv.updateVersionShareBubbles(versionId, this.session.current.user);
            this.prjSrv.updateFeedbacks(versionId);
            this.libSrv.updateVersionFeedbacks(versionId);
            this.approvalsSrv.updateVersionFeedbacks(versionId);
        });
    }

    private dashboard() {
        // this.dashSrv.reset();
        this.dashSrv.loadWorkflows();
        this.dashSrv.start(this.session.options.custom.dashboard_search, "session");
    }

    private library() {
        // this.libSrv.reset();
        this.libSrv.loadVersionsStatusAccess();
    }

    // dispatch change org
    changeOrganization(org: number)
    {
        this.dashSrv.reset();
        this.libSrv.reset();
        this.approvalsSrv.reset();
        this.appSrv.changeOrganization(org);
    }

}